import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserControllerApi } from '../api/generated';
import type {
  CreateUserRequest,
  UpdateUserRequest,
  UserDetailResponse
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { getErrorMessage } from '../utils/errorMessageUtils';
import { readRoles } from '../services/locationUserService';
import {
  fetchAccountPermissions,
  fetchLocationPermissions
} from '../services/permissionsService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectPermissionsLocationId } from '../store/permissions/permissionsSlice';
import { useCurrentUser } from './useCurrentUser';

export interface UseUsersResult {
  users: UserDetailResponse[];
  isLoading: boolean;
  loadError: string | null;
  // Re-reads the list for display. Failures surface through `loadError`, so a
  // caller that only re-renders has nothing to handle.
  refresh: () => Promise<void>;
  // Same fetch, but REJECTS if it fails and resolves with the fetched list.
  // Required by any caller that gates a WRITE on the result: silently diffing
  // against a stale list can revoke rights the user still holds elsewhere.
  fetchUsers: () => Promise<UserDetailResponse[]>;
  createUser: (payload: CreateUserRequest) => Promise<void>;
  updateUser: (userId: string, payload: UpdateUserRequest) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  // Nothing in the list changes, so there is no local state to patch: the password
  // and the pending password-change both live in Keycloak.
  resetPassword: (userId: string) => Promise<void>;
}

export function useUsers(): UseUsersResult {
  const [users, setUsers] = useState<UserDetailResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const api = useMemo(() => new UserControllerApi(createApiConfiguration()), []);
  const dispatch = useAppDispatch();
  const { id: currentUserId } = useCurrentUser();
  const activeLocationId = useAppSelector(selectPermissionsLocationId);

  /**
   * Re-reads the signed-in user's own permissions after a write that touched
   * their record.
   *
   * This page is reached through USER_ADMIN, and editing users is how that
   * permission is given away — an account admin who unticks their own admin box,
   * or drops their own central-administration role, may no longer open the very
   * page they are standing on. Without this they would keep reading other
   * people's rights until the next reload.
   *
   * Refetched rather than reasoned about locally: whether the write actually cost
   * them the permission depends on what they hold elsewhere (central
   * administration at another centre still grants USER_ADMIN), and only the
   * backend can resolve that. Once the store updates, the route guard redirects
   * on its own — nothing here needs to know which page is open.
   *
   * Both scopes, because a role assignment carries both: the account set decides
   * the guarded pages, the location set the controls inside them.
   */
  const refreshOwnPermissions = useCallback(
    async (userId: string): Promise<void> => {
      // Someone else's record cannot change what the signed-in user may do.
      if (!currentUserId || userId !== currentUserId) return;

      await fetchAccountPermissions(dispatch);
      if (activeLocationId) {
        await fetchLocationPermissions(dispatch, activeLocationId);
      }
    },
    [currentUserId, activeLocationId, dispatch]
  );

  const fetchUsers = useCallback(async (): Promise<UserDetailResponse[]> => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await api.getAllUsers();
      setUsers(response.data);
      return response.data;
    } catch (e) {
      console.error('Failed to load users:', e);
      setLoadError(getErrorMessage(e, 'Failed to load users'));
      // Rethrown rather than resolved with a fallback list: a caller about to
      // write permissions must be able to tell a fresh list from a stale one.
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const refresh = useCallback(async (): Promise<void> => {
    await fetchUsers().catch(() => undefined);
  }, [fetchUsers]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void refresh();
  }, [refresh]);

  const createUser = useCallback(
    async (payload: CreateUserRequest) => {
      const response = await api.createUser({ createUserRequest: payload });
      const created: UserDetailResponse = {
        userId: response.data.userId,
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        accountAdmin: payload.accountAdmin ?? false,
        roleAssignments: payload.roleAssignments.map((assignment) => ({
          locationId: assignment.locationId,
          roles: readRoles(assignment.roles)
        }))
      };
      setUsers((prev) => [...prev, created]);
    },
    [api]
  );

  const updateUser = useCallback(
    async (userId: string, payload: UpdateUserRequest) => {
      const response = await api.updateUser({ userId, updateUserRequest: payload });
      setUsers((prev) => prev.map((u) => (u.userId === userId ? response.data : u)));
      await refreshOwnPermissions(userId);
    },
    [api, refreshOwnPermissions]
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      await api.deleteUser({ userId });
      setUsers((prev) => prev.filter((u) => u.userId !== userId));
      await refreshOwnPermissions(userId);
    },
    [api, refreshOwnPermissions]
  );

  const resetPassword = useCallback(
    async (userId: string) => {
      await api.resetPassword({ userId });
    },
    [api]
  );

  return {
    users,
    isLoading,
    loadError,
    refresh,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    resetPassword
  };
}
